// API + WebSocket helpers for user trigger notifications.

import { buildApiUrl, ensureSecureRequestUrl, getAuthHeaders } from '../config/api';
import {
  EmailDiagnosticsResponse,
  InboundDiagnosticsResponse,
  NotificationPreferences,
  NotificationPreferencesResponse,
  TestNotificationResponse,
} from '../types/notifications';

const PREFERENCES_PATH = '/api/v1/notifications/preferences';
const TEST_PATH = '/api/v1/notifications/test';
const EMAIL_DIAGNOSTICS_PATH = '/api/v1/notifications/email-diagnostics';
const INBOUND_DIAGNOSTICS_PATH = '/api/v1/notifications/inbound-diagnostics';

function sessionHeaders(explicit?: string): Record<string, string> {
  const jwt = explicit && explicit.split('.').length === 3 ? explicit : undefined;
  const headers = getAuthHeaders(jwt);
  if (typeof document !== 'undefined') {
    const match = document.cookie.match(/(?:^|; )br_csrf=([^;]*)/);
    if (match) headers['X-CSRF-Token'] = decodeURIComponent(match[1]);
  }
  return headers;
}

export async function getNotificationPreferences(
  token?: string
): Promise<NotificationPreferencesResponse> {
  const url = ensureSecureRequestUrl(buildApiUrl(PREFERENCES_PATH));
  const res = await fetch(url, { credentials: 'include', headers: sessionHeaders(token) });
  if (!res.ok) {
    throw new Error(`Failed to load notification preferences (${res.status})`);
  }
  return res.json();
}

export async function updateNotificationPreferences(
  preferences: NotificationPreferences,
  token?: string
): Promise<NotificationPreferencesResponse> {
  const url = ensureSecureRequestUrl(buildApiUrl(PREFERENCES_PATH));
  const res = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: sessionHeaders(token),
    body: JSON.stringify(preferences),
  });
  if (!res.ok) {
    throw new Error(`Failed to save notification preferences (${res.status})`);
  }
  return res.json();
}

export async function sendTestNotification(token?: string): Promise<TestNotificationResponse> {
  const url = ensureSecureRequestUrl(buildApiUrl(TEST_PATH));
  const res = await fetch(url, { method: 'POST', credentials: 'include', headers: sessionHeaders(token) });
  if (!res.ok) {
    throw new Error(`Failed to send test notification (${res.status})`);
  }
  return res.json();
}

/**
 * Probe the outbound email (SMTP) pipeline step by step.
 * Pass send=true to also send a real diagnostic email to your account address.
 */
export async function runEmailDiagnostics(
  send = false,
  to?: string,
  token?: string
): Promise<EmailDiagnosticsResponse> {
  const params = new URLSearchParams({ send: String(send) });
  if (to && to.trim()) params.set('to', to.trim());
  const url = ensureSecureRequestUrl(buildApiUrl(`${EMAIL_DIAGNOSTICS_PATH}?${params.toString()}`));
  const res = await fetch(url, { method: 'POST', credentials: 'include', headers: sessionHeaders(token) });
  if (!res.ok) {
    throw new Error(`Failed to run email diagnostics (${res.status})`);
  }
  return res.json();
}

/**
 * Probe the inbound (IMAP) mailbox pipeline step by step. Confirms the shared
 * mailbox is reachable and reports the alias address(es) registered for the user.
 */
export async function runInboundDiagnostics(
  token?: string
): Promise<InboundDiagnosticsResponse> {
  const url = ensureSecureRequestUrl(buildApiUrl(INBOUND_DIAGNOSTICS_PATH));
  const res = await fetch(url, { method: 'POST', credentials: 'include', headers: sessionHeaders(token) });
  if (!res.ok) {
    throw new Error(`Failed to run inbound diagnostics (${res.status})`);
  }
  return res.json();
}

/** Build the wss/ws URL for the notification channel (mirrors websocketService host logic). */
export function buildNotificationWsUrl(tenantId: string, userId: number, token: string): string {
  const local =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  let base: string;
  if (process.env.NODE_ENV === 'development' && local) {
    const port = window.location.port;
    if (port === '8080' || port === '8443') {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      base = `${protocol}//${window.location.host}`;
    } else {
      base = 'ws://localhost:8000';
    }
  } else {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    base = `${protocol}//${window.location.host}`;
  }

  const params = new URLSearchParams({ user_id: String(userId), token });
  return `${base}/api/v1/notifications/ws/${encodeURIComponent(tenantId)}?${params.toString()}`;
}
